/**
 * @numi/engine — the maths brain of Numi.
 * Everything here is pure and deterministic apart from randomness in question generation,
 * so it runs identically in the browser and on the server.
 */
import * as coreMod from "./core.ts";
import * as curMod from "./curriculum.ts";
const cur: any = curMod;
// The ported core is untyped; this file is its typed contract.
const core: any = coreMod;

export type BoardId = "cbse" | "icse" | "uae" | "gen";
export type Grade = 1 | 2 | 3 | 4 | 5;
export type Level = 1 | 2 | 3;

export interface Board {
  name: string; gradeWord: "Class" | "Grade"; locale: string; indian: boolean;
  sym: string; cur: string; coins: number[]; notes: number[]; names: string[];
  region: string; things: string;
}

/** A question as produced by a topic generator or a hands-on builder. */
export interface Question {
  tid?: string;
  type: "num" | "choice" | "build";
  /** HTML (may contain fraction markup and highlight spans). */
  text: string;
  /** Plain-text version for read-aloud and for Claude. */
  say?: string;
  /** SVG/HTML string of the picture, if any. */
  viz?: string;
  answer: number | string;
  choices?: string[];
  frac?: boolean;
  dec?: boolean;
  unit?: string;
  hints: string[];
  explain: string;
  /** For story mode: what the child must work out, and the numbers that must appear. */
  seed?: string;
  nums?: number[];
  /** Hands-on questions */
  build?: "frame" | "blocks" | "share" | "clock" | "shade" | "abacus";
  st?: any;
  [k: string]: unknown;
}

export interface LessonStep { say: string | (() => string); viz: () => string; }
export interface Topic {
  /** g = 0 for special modules that work across classes (e.g. Learning Abacus). */
  id: string; g: Grade | 0; title: string | (() => string);
  /** Unit key (see UNITS); mod = "vedic" for Vedic Maths tricks, available from class minG. */
  u?: string; mod?: string; minG?: number; sutra?: string;
  lesson: LessonStep[];
  gen: (level: Level) => Question;
}

export const BOARDS = core.BOARDS as Record<BoardId, Board>;
export const setBoard: (b: BoardId) => void = core.setBoard;
export const board: () => Board = core.ctx;
export const TOPICS = core.TOPICS as Topic[];
export const topic: (id: string) => Topic | undefined = core.T;
export const topicsFor: (g: number) => Topic[] = core.topicsFor;
export const topicTitle: (t: Topic) => string = core.title;
export const stepText: (v: string | (() => string)) => string = core.txt;
export const topicIcon: (t: Topic) => string = core.icon;

export const BUILD_TOPICS = core.BUILD_TOPICS as Set<string>;
export const makeBuild: (tid: string, level: Level) => Question | null = core.makeBuild;
export const buildHTML: (q: Question) => string = core.buildHTML;
export const buildValue: (q: Question) => number | string | null = core.buildValue;

export const diagnose: (q: Question, value: string | number) => string | null = core.diagnose;
export const praise: (q: Question, firstTry: boolean) => string = core.praise;
export const closeness: (q: Question, value: string | number) => string = core.closeness;
export const valEq: (a: unknown, b: unknown) => boolean = core.valEq;

export const bubbleOptions: (q: Question) => string[] = core.bubOptions;
export const bubbleLabel: (v: string, q: Question) => string = core.bubLabel;

/** Safe arithmetic evaluator used as Claude's calculator tool. Throws on anything but + - * / ( ) and numbers. */
export const calcEval: (expr: string) => number = core.calcEval;

export const STUCK = core.STUCK as Record<"q" | "start" | "diff", { label: string; short: string; icon: string }>;
export const STICKERS = core.STICKERS as [string, string][];
export const BREAKS = core.BREAKS as string[];
export const STRAT = core.STRAT as Record<string, string>;

export const V = core.V;
/** Special modules shown for every class. */
export const SPECIAL_TOPICS = ["abacus"];
export const UNITS = cur.UNITS as Record<string, string>;
export const vedicFor: (g: number) => Topic[] = cur.vedicFor;
/** Spot a Vedic shortcut relevant to a just-answered regular question, gated by the child's grade. */
export const smartTip: (tid: string, q: Question, grade: number) => { id: string; title: string; sutra: string; text: string } | null = cur.smartTip;
/** Tap a bead on rod r (b = "u" for the top bead, or 0–3 for bottom beads). Mutates and returns the digits. */
export const abacusTap: (digits: number[], rod: number, bead: string) => number[] = core.abTap;
export const I = core.I as Record<string, string>;
export const numiSVG: (size: number, id?: string, extra?: string) => string = core.numiSVG;
export const starSVG: (on?: boolean) => string = core.starSVG;
export const fracHTML: (s: string) => string = core.fracStr;
export const esc: (s: unknown) => string = core.esc;
export const pick: <T>(a: T[]) => T = core.pick;
export const shuffle: <T>(a: T[]) => T[] = core.shuffle;
export const fmt: (n: number) => string = core.fmt;

/**
 * Hints must nudge, never hand over the answer. If a hint states the final answer as the result of a step
 * ("= 85", "→ 112", "Double again: 196") the number is replaced with "?" so the child still has to do the last step.
 * Only results are masked; numbers the child is given (e.g. "between 30 and 40") are left alone.
 */
export function scrubHints(q: Question): Question {
  if (q.type === "build" || !q.hints?.length) return q;
  const raw = String(q.answer).replace(/,/g, "");
  if (!/^\d+(\.\d+)?$/.test(raw) || raw.replace(".", "").length < 2) return q;
  const forms = Array.from(new Set([raw, Number(raw) >= 1000 && !raw.includes(".") ? fmt(Number(raw)) : raw]));
  const rx = forms.map((f) => new RegExp(`((?:=|→|:)\\s*[^\\d\\s?]{0,4}\\s?)${f.replace(/[.,]/g, "\\$&")}(?![\\d]|,\\d|\\.\\d)\\??`, "g"));
  q.hints = q.hints.map((h) => rx.reduce((s, r) => s.replace(r, "$1?"), h));
  return q;
}
// Every generator goes through the scrubber, so no code path (practice, bubbles, warm-ups) can leak an answer in a hint.
for (const t of TOPICS) { const gen = t.gen; t.gen = (L) => scrubHints(gen(L)); }

/** Generate the next practice question for a topic, mixing in hands-on questions where available. */
export function nextQuestion(tid: string, level: Level, opts: { buildChance?: number } = {}): Question {
  const t = topic(tid);
  if (!t) throw new Error(`Unknown topic ${tid}`);
  const chance = opts.buildChance ?? (tid === "abacus" ? 0.6 : 0.4);
  let q: Question | null = null;
  if (BUILD_TOPICS.has(tid) && Math.random() < chance) q = makeBuild(tid, level);
  if (!q) q = t.gen(level);
  q.tid = tid;
  return q;
}

/** Is this value the right answer? Handles numbers, choices and clock strings. */
export function isCorrect(q: Question, value: string | number): boolean {
  if (q.type === "choice" || q.build === "clock") return String(value) === String(q.answer);
  return valEq(value, q.answer);
}

/** Strip HTML to plain text for speech and prompts. */
export const plain = (h: string) => String(h).replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();

/* Learning comfort (parent-set adjustments) and pacing (noticing a struggle). */
export * from "./comfort.ts";
